import { useEffect, useMemo, useRef } from "react";
import { ArrowLeft, Search, Check, UserPlus, Clock, ShieldAlert, Plus, Loader2, UserRound, Users } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const TEAM_FILTERS = [
    { id: "all", label: "All" },
    { id: "main", label: "Main" },
    { id: "crowd", label: "Crowd" },
];

/**
 * Full-screen multi-select character picker for AssignmentDialog.
 * Same prop pattern as the scene picker — the parent owns state.
 */
export default function AssignmentCharacterPicker({
    open,
    onClose,
    characters,
    filteredCharacters,
    selectedIds,
    onToggle,
    onClear,
    teamFilter,
    setTeamFilter,
    query,
    setQuery,
    loading,
    // Inline-create wiring
    canCreateCharacters,
    creatingChar,
    setCreatingChar,
    hasPendingRequest,
    requestingPerm,
    onRequestPerm,
    newCharNumber,
    setNewCharNumber,
    newCharName,
    setNewCharName,
    newCharActor,
    setNewCharActor,
    newCharTeam,
    setNewCharTeam,
    creatingCharSubmitting,
    onCreateCharacter,
}) {
    const inputRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const t = setTimeout(() => inputRef.current?.focus(), 60);
        return () => clearTimeout(t);
    }, [open]);

    const selectedCount = selectedIds.length;

    const summary = useMemo(() => {
        if (selectedCount === 0) return null;
        const names = selectedIds
            .map((id) => characters.find((x) => x.id === id)?.name)
            .filter(Boolean);
        if (names.length === 0) return null;
        if (names.length <= 2) return names.join(", ");
        return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
    }, [selectedIds, characters, selectedCount]);

    if (!open) return null;

    return (
        <>
            {/* Desktop backdrop dimmer — hidden on mobile where the
                picker fills the full viewport already. */}
            <div
                aria-hidden="true"
                className="hidden sm:block fixed inset-0 z-[59] bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />
            <div
                data-testid="character-picker"
                className="fixed inset-0 z-[60] bg-card text-white flex flex-col sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-w-lg sm:w-full sm:max-h-[88vh] sm:rounded-xl sm:border sm:border-white/10 sm:shadow-2xl"
                role="dialog"
                aria-modal="true"
                aria-label="Pick characters"
            >
            {/* Header */}
            <div className="px-3 pt-3 pb-2 border-b border-white/10 flex items-center gap-2">
                <button
                    type="button"
                    data-testid="character-picker-back"
                    onClick={onClose}
                    className="instant-tap h-10 w-10 inline-flex items-center justify-center rounded-md text-neutral-300 hover:text-white hover:bg-white/10"
                    aria-label="Back"
                >
                    <ArrowLeft size={20} />
                </button>
                <div className="flex-1 min-w-0">
                    <h2 className="font-display text-base font-bold leading-tight">
                        Pick characters
                    </h2>
                    <p className="text-[11px] text-neutral-400 leading-snug truncate">
                        {selectedCount > 0
                            ? `${selectedCount} selected${summary ? ` · ${summary}` : ""}`
                            : "Tap to multi-select"}
                    </p>
                </div>
                {selectedCount > 0 ? (
                    <button
                        type="button"
                        data-testid="character-picker-clear"
                        onClick={onClear}
                        className="instant-tap text-xs text-neutral-300 hover:text-white px-2 h-9 rounded-md hover:bg-white/10"
                    >
                        Clear
                    </button>
                ) : null}
            </div>

            {/* Search + create CTA */}
            <div className="px-3 pt-3 pb-2 space-y-2">
                <div className="relative">
                    <Search
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
                    />
                    <Input
                        ref={inputRef}
                        data-testid="character-picker-search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search cast (name, actor or #)"
                        className="pl-9 h-11 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary text-base"
                    />
                </div>

                <div className="flex items-center justify-between gap-2">
                    {/* Team chips */}
                    <div
                        data-testid="character-picker-team-filter"
                        role="tablist"
                        className="flex gap-1.5 flex-wrap"
                    >
                        {TEAM_FILTERS.map((f) => {
                            const count =
                                f.id === "all"
                                    ? characters.length
                                    : characters.filter(
                                          (c) => (c.team || "main") === f.id,
                                      ).length;
                            return (
                                <button
                                    key={f.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={teamFilter === f.id}
                                    data-testid={`character-picker-team-${f.id}`}
                                    data-active={teamFilter === f.id ? "true" : "false"}
                                    onClick={() => setTeamFilter(f.id)}
                                    className={`instant-tap h-8 px-3 rounded-full text-[11px] uppercase tracking-[0.15em] border ${
                                        teamFilter === f.id
                                            ? "bg-primary text-primary-foreground border-primary"
                                            : "bg-secondary text-neutral-300 border-white/10 hover:bg-white/10"
                                    }`}
                                >
                                    {f.label}
                                    <span
                                        className={`ml-1.5 ${
                                            teamFilter === f.id
                                                ? "text-primary-foreground/70"
                                                : "text-neutral-500"
                                        }`}
                                    >
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Inline-create / request perm */}
                    {canCreateCharacters ? (
                        <button
                            type="button"
                            data-testid="character-picker-toggle-create"
                            onClick={() => setCreatingChar((v) => !v)}
                            className="tap text-xs text-neutral-300 hover:text-white inline-flex items-center gap-1 shrink-0"
                        >
                            <UserPlus size={12} />
                            {creatingChar ? "Cancel" : "+ New"}
                        </button>
                    ) : hasPendingRequest ? (
                        <span
                            data-testid="character-picker-perm-pending"
                            className="text-[11px] text-amber-300 inline-flex items-center gap-1 shrink-0"
                        >
                            <Clock size={11} /> Pending
                        </span>
                    ) : (
                        <button
                            type="button"
                            data-testid="character-picker-request-perm"
                            onClick={onRequestPerm}
                            disabled={requestingPerm}
                            className="tap text-xs text-neutral-400 hover:text-white inline-flex items-center gap-1 shrink-0"
                        >
                            <ShieldAlert size={11} />
                            {requestingPerm ? "Sending…" : "Request"}
                        </button>
                    )}
                </div>

                {creatingChar && canCreateCharacters ? (
                    <form
                        data-testid="character-picker-new-form"
                        onSubmit={onCreateCharacter}
                        className="space-y-2 rounded-lg border border-white/10 bg-secondary/40 p-2"
                    >
                        <div className="grid grid-cols-3 gap-2">
                            <Input
                                data-testid="character-picker-new-number"
                                placeholder="#"
                                value={newCharNumber}
                                onChange={(e) => setNewCharNumber(e.target.value)}
                                maxLength={40}
                                className="h-10 col-span-1 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary text-base"
                            />
                            <Input
                                data-testid="character-picker-new-name"
                                placeholder="Name (e.g. Bob)"
                                value={newCharName}
                                onChange={(e) => setNewCharName(e.target.value)}
                                maxLength={80}
                                autoFocus
                                className="h-10 col-span-2 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary text-base"
                            />
                        </div>
                        <Input
                            data-testid="character-picker-new-actor"
                            placeholder="Actor (optional)"
                            value={newCharActor}
                            onChange={(e) => setNewCharActor(e.target.value)}
                            maxLength={80}
                            className="h-10 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary text-base"
                        />
                        <div
                            data-testid="character-picker-new-team"
                            className="grid grid-cols-2 p-1 bg-secondary rounded-md gap-1"
                        >
                            {[
                                { id: "main", label: "Main", icon: UserRound },
                                { id: "crowd", label: "Crowd", icon: Users },
                            ].map(({ id, label, icon: Icon }) => (
                                <button
                                    key={id}
                                    type="button"
                                    data-testid={`character-picker-new-team-${id}`}
                                    data-active={newCharTeam === id ? "true" : "false"}
                                    aria-pressed={newCharTeam === id}
                                    onClick={() => setNewCharTeam(id)}
                                    className={`instant-tap h-9 rounded text-xs font-semibold inline-flex items-center justify-center gap-1.5 ${
                                        newCharTeam === id
                                            ? "bg-primary text-primary-foreground shadow-sm"
                                            : "text-neutral-300 hover:text-white"
                                    }`}
                                >
                                    <Icon size={12} />
                                    {label}
                                </button>
                            ))}
                        </div>
                        <Button
                            type="submit"
                            data-testid="character-picker-new-submit"
                            disabled={!newCharName.trim() || creatingCharSubmitting}
                            className="w-full h-10 font-semibold"
                        >
                            {creatingCharSubmitting ? (
                                <>
                                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                                    Adding…
                                </>
                            ) : (
                                <>
                                    <Plus size={14} className="mr-1.5" />
                                    Add &amp; select
                                </>
                            )}
                        </Button>
                    </form>
                ) : null}
            </div>

            {/* List */}
            <div
                data-testid="character-picker-list"
                className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5 min-h-0"
            >
                {loading ? (
                    <p className="text-xs text-neutral-500 px-1 py-2">Loading cast…</p>
                ) : characters.length === 0 ? (
                    <p className="text-xs text-neutral-500 px-1 py-2">
                        {canCreateCharacters
                            ? "No characters yet — tap \u201c+ New\u201d above."
                            : "No characters yet. Ask the project owner to add cast."}
                    </p>
                ) : filteredCharacters.length === 0 ? (
                    <p
                        data-testid="character-picker-empty"
                        className="text-xs text-neutral-500 px-1 py-2"
                    >
                        {query
                            ? `No cast matches "${query}".`
                            : "No cast in this team yet."}
                    </p>
                ) : (
                    filteredCharacters.map((c) => {
                        const isSelected = selectedIds.includes(c.id);
                        return (
                            <button
                                key={c.id}
                                type="button"
                                data-testid={`character-picker-row-${c.id}`}
                                data-active={isSelected ? "true" : "false"}
                                aria-pressed={isSelected}
                                onClick={() => onToggle(c.id)}
                                className={`instant-tap w-full min-h-[52px] text-left flex items-center gap-3 px-3 py-2 rounded-lg text-sm border ${
                                    isSelected
                                        ? "bg-primary/15 text-white border-primary"
                                        : "bg-secondary text-neutral-200 border-white/10 hover:bg-white/10"
                                }`}
                            >
                                {c.character_number ? (
                                    <span
                                        className={`mono text-[11px] min-w-[2.5rem] px-1 text-center shrink-0 ${
                                            isSelected ? "text-white/80" : "text-neutral-500"
                                        }`}
                                    >
                                        #{c.character_number}
                                    </span>
                                ) : (
                                    <span className="min-w-[2.5rem] shrink-0" aria-hidden="true" />
                                )}
                                <span className="flex-1 truncate">
                                    {c.name}
                                    {c.actor ? (
                                        <span
                                            className={`ml-1 text-[11px] ${
                                                isSelected
                                                    ? "text-white/70"
                                                    : "text-neutral-500"
                                            }`}
                                        >
                                            · {c.actor}
                                        </span>
                                    ) : null}
                                </span>
                                <span
                                    className={`h-5 w-5 rounded-md border inline-flex items-center justify-center shrink-0 ${
                                        isSelected
                                            ? "bg-primary border-primary text-primary-foreground"
                                            : "border-white/20"
                                    }`}
                                    aria-hidden="true"
                                >
                                    {isSelected ? <Check size={12} strokeWidth={3} /> : null}
                                </span>
                            </button>
                        );
                    })
                )}
            </div>

            {/* Footer */}
            <div className="px-3 py-3 border-t border-white/10 bg-card">
                <Button
                    type="button"
                    data-testid="character-picker-done"
                    onClick={onClose}
                    className="w-full h-11 font-semibold tracking-wide"
                >
                    {selectedCount > 0
                        ? `Done · ${selectedCount} selected`
                        : "Done"}
                </Button>
            </div>
            </div>
        </>
    );
}
